import { BING_SYSTEM_IDENTITY, type InboxMessageView } from "@bing/shared";
import { useEffect, useState, type ReactNode } from "react";
import logoUrl from "../assets/logo.jpg";
import { formatWhen } from "../components/GameStats";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageShell } from "../components/PageShell";
import { inboxImageUrl } from "../components/profile-ui";
import { RichText } from "../components/RichText";
import { OfficialBadge, Spinner } from "../components/social-ui";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";

/** Trash glyph for the per-message delete affordance (inline `currentColor`). */
function TrashIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor" aria-hidden="true">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}

/** The «@bing» sender chip — brand disc + name + verified badge + timestamp. */
function BingSender({ createdAt }: { createdAt: string }): ReactNode {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <img
        src={logoUrl}
        alt=""
        width={28}
        height={28}
        className="rounded-full object-cover"
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,.3)" }}
      />
      <span className="truncate font-body-lg text-body-lg text-on-surface">
        {BING_SYSTEM_IDENTITY.displayName}
      </span>
      <OfficialBadge size={15} />
      <span className="ms-auto whitespace-nowrap font-caption text-caption text-on-surface-variant">
        {formatWhen(createdAt)}
      </span>
    </div>
  );
}

/** A single inbox message card — the @bing sender header (with a delete button), then the
 *  (optional) title + text. */
function MessageCard({
  m,
  onDelete,
}: {
  m: InboxMessageView;
  onDelete: (id: string) => void;
}): ReactNode {
  return (
    <article
      className={`flex flex-col gap-3 rounded-2xl border p-lg ${
        m.read
          ? "border-outline-variant/30 bg-surface-container"
          : "border-secondary/50 bg-surface-container-high"
      }`}
    >
      <div className="flex items-center gap-2">
        <BingSender createdAt={m.createdAt} />
        <button
          type="button"
          onClick={() => onDelete(m.id)}
          aria-label={fa.messages.deleteOne}
          className="flex h-8 w-8 flex-none appearance-none cursor-pointer items-center justify-center rounded-full bg-surface-container-highest/70 text-on-surface-variant transition-colors hover:bg-error-container hover:text-on-error-container active:scale-90"
        >
          <TrashIcon />
        </button>
      </div>
      {m.imageId ? (
        <img
          src={inboxImageUrl(m.imageId)}
          alt=""
          loading="lazy"
          className="max-h-80 w-full rounded-xl object-cover"
        />
      ) : null}
      {m.title ? (
        <h3 className="m-0 font-headline-sm text-headline-sm text-on-surface">{m.title}</h3>
      ) : null}
      {m.text ? (
        <p className="m-0 whitespace-pre-wrap break-words font-body-md text-body-md leading-relaxed text-on-surface-variant">
          <RichText text={m.text} entities={m.entities ?? []} />
        </p>
      ) : null}
    </article>
  );
}

/**
 * The in-app Messages screen — «@bing» broadcasts (system notices, not bot DMs). Every
 * message is rendered as sent by the official «@bing» identity (brand disc + verified
 * badge). Opening the screen marks everything read (loadInbox). The user can delete a
 * single message (trash button on each card) or clear the whole inbox («پاک کردن همه»,
 * confirm-guarded). RTL, theme-aware.
 */
export function Messages(): ReactNode {
  // ITEM 13 — back goes where the user CAME FROM, like Settings and History. Falling
  // through to PageShell's hardcoded lobby is right only while the drawer is the sole
  // way in; the moment anything else opens this screen it is silently wrong.
  const setView = useSession((st) => st.setView);
  const pageReturn = useSession((st) => st.pageReturn);
  const inbox = useSession((s) => s.inbox);
  const loadInbox = useSession((s) => s.loadInbox);
  const deleteInboxMessage = useSession((s) => s.deleteInboxMessage);
  const clearInbox = useSession((s) => s.clearInbox);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    loadInbox()
      .then(() => {
        if (!cancelled) setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, [loadInbox]);

  return (
    <PageShell title={fa.messages.title} icon="mail" onBack={() => setView(pageReturn)}>
      {phase === "loading" ? (
        <div className="flex flex-col items-center gap-sm py-2xl text-on-surface-variant">
          <Spinner />
          <span className="font-body-md text-body-md">{fa.messages.loading}</span>
        </div>
      ) : phase === "error" ? (
        <div className="flex flex-col items-center gap-md py-2xl text-center">
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            {fa.messages.error}
          </p>
          <button
            type="button"
            onClick={() => {
              setPhase("loading");
              void loadInbox()
                .then(() => setPhase("ready"))
                .catch(() => setPhase("error"));
            }}
            className="appearance-none cursor-pointer rounded-lg border border-outline-variant bg-surface-container-high px-lg py-sm font-label-md text-label-md text-on-surface transition-colors hover:bg-surface-variant"
          >
            {fa.messages.retry}
          </button>
        </div>
      ) : inbox.length === 0 ? (
        // Roomy empty state. `py-xl` = 32px REAL vertical padding (`py-2xl` was a no-op — no `2xl` in
        // the spacing scale, so the box had none). The hint is one line: `caption` (12px; 14px is
        // physically too wide for a phone box) + `whitespace-nowrap`, no artificial `max-w` to wrap it.
        <div className="mt-lg flex flex-col items-center gap-4 rounded-2xl border border-dashed border-outline-variant/60 bg-surface-container-low px-md py-xl text-center">
          <span
            aria-hidden="true"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-container text-4xl"
          >
            📭
          </span>
          <p className="m-0 font-headline-sm text-headline-sm text-on-surface">
            {fa.messages.empty}
          </p>
          <p className="m-0 whitespace-nowrap font-caption text-caption text-on-surface-variant">
            {fa.messages.emptyHint}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-md">
          {/* Action bar: message count on the start, the destructive «پاک کردن همه» chip on the
              end (the inline edge the per-card trash buttons also sit on, so deletes line up). The
              chip is error-tinted so a bulk wipe reads as destructive, distinct from a single trash. */}
          <div className="flex items-center justify-between gap-2">
            <span className="ps-1 font-caption text-caption text-on-surface-variant">
              {faDigits(inbox.length)} {fa.messages.count}
            </span>
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              className="appearance-none inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-error/30 bg-error-container/60 px-3.5 py-1.5 font-label-md text-label-md text-on-error-container shadow-sm transition-transform active:scale-95 hover:bg-error-container"
            >
              <TrashIcon />
              {fa.messages.clearAll}
            </button>
          </div>
          {inbox.map((m) => (
            <MessageCard
              key={m.id}
              m={m}
              onDelete={(id) => {
                void deleteInboxMessage(id).catch(() => {
                  // Best-effort: the store restores the card on failure (server rejected).
                });
              }}
            />
          ))}
        </div>
      )}
      {confirmClear ? (
        <ConfirmDialog
          title={fa.messages.clearAllTitle}
          body={fa.messages.clearAllBody}
          confirmLabel={fa.messages.clearAllConfirm}
          cancelLabel={fa.messages.clearAllCancel}
          onConfirm={() => {
            void clearInbox().catch(() => {
              // Best-effort: the store restores the list on failure.
            });
            setConfirmClear(false);
          }}
          onCancel={() => setConfirmClear(false)}
        />
      ) : null}
    </PageShell>
  );
}
